import React, { forwardRef, useId } from "react";
import { HiOutlineChevronDown } from "react-icons/hi2";
import cn from "./cn";

export const controlClassName = ({ invalid, className, size = "md" } = {}) =>
  cn(
    "block w-full rounded-lg border bg-white text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 read-only:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
    size === "sm" ? "h-9 px-3 text-sm" : "h-10 px-3.5 text-sm",
    invalid
      ? "border-rose-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
      : "border-slate-300 hover:border-slate-400 focus:border-brand-500 focus:ring-4 focus:ring-brand-100",
    className,
  );

export const Label = ({ htmlFor, required, optional, children, className, hint }) => (
  <label htmlFor={htmlFor} className={cn("mb-1.5 flex items-center gap-1 text-sm font-medium text-slate-700", className)}>
    <span>{children}</span>
    {required ? (
      <span className="text-rose-600" aria-hidden="true">
        *
      </span>
    ) : null}
    {optional ? <span className="text-xs font-normal text-slate-400">({optional})</span> : null}
    {hint ? <span className="ms-auto text-xs font-normal text-slate-400">{hint}</span> : null}
  </label>
);

export const HelpText = ({ id, children, className }) =>
  children ? (
    <p id={id} className={cn("mt-1.5 text-xs leading-5 text-slate-500", className)}>
      {children}
    </p>
  ) : null;

export const FieldError = ({ id, children, className }) =>
  children ? (
    <p id={id} role="alert" className={cn("mt-1.5 text-xs font-medium leading-5 text-rose-600", className)}>
      {children}
    </p>
  ) : null;

// Field: label + control + help/error wiring (aria-describedby, aria-invalid).
// `children` is a render function receiving the computed control props, or a
// React element that will be cloned with them.
export const Field = ({
  id: providedId,
  label,
  required,
  optional,
  help,
  error,
  hint,
  className,
  labelClassName,
  children,
}) => {
  const generatedId = useId();
  const id = providedId || `field-${generatedId}`;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const controlProps = {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": [errorId, helpId].filter(Boolean).join(" ") || undefined,
    "aria-required": required || undefined,
    invalid: Boolean(error),
  };

  return (
    <div className={cn("min-w-0", className)}>
      {label ? (
        <Label htmlFor={id} required={required} optional={optional} hint={hint} className={labelClassName}>
          {label}
        </Label>
      ) : null}
      {typeof children === "function"
        ? children(controlProps)
        : React.isValidElement(children)
          ? React.cloneElement(children, controlProps)
          : children}
      <FieldError id={errorId}>{error}</FieldError>
      <HelpText id={helpId}>{help}</HelpText>
    </div>
  );
};

export const Input = forwardRef(({ invalid, className, size, ltr, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(controlClassName({ invalid, className, size }), ltr && "ltr-value")}
    data-ltr={ltr ? "true" : undefined}
    {...props}
  />
));
Input.displayName = "Input";

export const Textarea = forwardRef(({ invalid, className, rows = 3, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={cn(controlClassName({ invalid, className }), "h-auto py-2.5 leading-6")}
    {...props}
  />
));
Textarea.displayName = "Textarea";

export const Select = forwardRef(({ invalid, className, size, children, ...props }, ref) => (
  <span className="relative block">
    <select
      ref={ref}
      className={cn(controlClassName({ invalid, className, size }), "cursor-pointer appearance-none pe-9")}
      {...props}
    >
      {children}
    </select>
    <HiOutlineChevronDown
      aria-hidden="true"
      className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-slate-400"
      size={16}
    />
  </span>
));
Select.displayName = "Select";

export const Checkbox = forwardRef(({ label, description, className, id: providedId, ...props }, ref) => {
  const generatedId = useId();
  const id = providedId || `checkbox-${generatedId}`;
  return (
    <label htmlFor={id} className={cn("flex cursor-pointer items-start gap-2.5 text-sm text-slate-700", className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-brand-700 accent-brand-700 focus:ring-brand-200"
        {...props}
      />
      {label || description ? (
        <span className="min-w-0">
          {label ? <span className="block font-medium">{label}</span> : null}
          {description ? <span className="block text-xs text-slate-500">{description}</span> : null}
        </span>
      ) : null}
    </label>
  );
});
Checkbox.displayName = "Checkbox";

// Accessible switch (role="switch") used for enable/disable toggles.
export const Switch = ({ checked, onChange, label, description, disabled, className, id: providedId, size = "md" }) => {
  const generatedId = useId();
  const id = providedId || `switch-${generatedId}`;
  const track = size === "sm" ? "h-5 w-9" : "h-6 w-11";
  const knob = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const translate = size === "sm" ? "translate-x-4 rtl:-translate-x-4" : "translate-x-5 rtl:-translate-x-5";
  return (
    <div className={cn("flex items-start gap-3", className)}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={!label && !description ? undefined : undefined}
        aria-labelledby={label ? `${id}-label` : undefined}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={cn(
          "relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-200 disabled:cursor-not-allowed disabled:opacity-50",
          track,
          checked ? "bg-brand-600" : "bg-slate-300",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none inline-block transform rounded-full bg-white shadow transition",
            knob,
            checked ? translate : "translate-x-0",
          )}
        />
      </button>
      {label || description ? (
        <span className="min-w-0">
          {label ? (
            <span id={`${id}-label`} className="block text-sm font-medium text-slate-800">
              {label}
            </span>
          ) : null}
          {description ? <span className="block text-xs text-slate-500">{description}</span> : null}
        </span>
      ) : null}
    </div>
  );
};

// Groups related fields with a heading; use inside forms for sections.
export const FormSection = ({ title, description, children, className, actions }) => (
  <section className={cn("rounded-xl border border-slate-200 bg-white", className)}>
    {title || actions ? (
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="min-w-0">
          {title ? <h2 className="text-sm font-semibold text-slate-900">{title}</h2> : null}
          {description ? <p className="mt-0.5 text-xs leading-5 text-slate-500">{description}</p> : null}
        </div>
        {actions ? <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div> : null}
      </header>
    ) : null}
    {/* grid-cols-1 = minmax(0, 1fr): a wide child (airline matrix) scrolls in
        its own container instead of widening the page on phones. */}
    <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">{children}</div>
  </section>
);

// Sticky-ish action bar for forms: primary on the end side, cancel first.
export const FormActions = ({ children, className, align = "end" }) => (
  <div
    className={cn(
      "flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4",
      align === "end" ? "justify-end" : align === "between" ? "justify-between" : "justify-start",
      className,
    )}
  >
    {children}
  </div>
);
